/**
 * The **mine** spawn adapters — the explicit-profile mine (riding the SIZE-1 object primitive)
 * and the mine-from-pilot-gear path that derives that profile off a compendium deployable.
 */

import type { Compendium } from "@lancer-vtt/compendium";
import type { DamageType, MineProfile, Side } from "@lancer-vtt/domain";
import { ok, err, type Result } from "@lancer-vtt/shared";
import { normalizeDamageType } from "../weaponProfile";
import { spawnObject, type SpawnObjectArgs } from "./object";
import type { RawPilotGear } from "./pilot";
import type { SpawnResult } from "./types";

/**
 * Args for {@link spawnMine}. Like {@link spawnObject}, a mine has no frame/class to
 * freeze *from* — the MINE tag (Core p.106) gives no fixed damage/radius/save, so a
 * mine-tagged system authors its own detonation payload and the caller names it
 * directly, rather than a compendium/roster reference.
 */
export type SpawnMineArgs = {
  combatantId: string;
  name: string;
  /** The BURST radius the detonation covers — see {@link "@lancer-vtt/domain".MineProfile.burstRadius}. */
  burstRadius: number;
  /**
   * Pre-mitigation damage dealt to each hit character — a flat number (Thermite Charge's `3`)
   * or a **rolled** dice spec `{ count, sides }` (HEX Charges' Explosive Mine: `2d6`), mirroring
   * {@link "@lancer-vtt/domain".MineProfile.damage}'s shape EXACTLY — this field is assigned
   * straight onto that one (see below), so the two must stay the same shape.
   * `resolveMineDetonation` (not this function) does the rolling; `spawnMine` only validates
   * and passes the spec through unrolled.
   *
   * **`undefined` is a genuinely damageless mine** — `resolveMineDetonation` skips the
   * character-damage arm entirely (no roll, no save, no `DamageStep`, no `hp_delta`), rather
   * than dealing `0`; see {@link "@lancer-vtt/domain".MineProfile.damage}'s doc for the full
   * rationale. {@link objectDamage} is independent and may still be set on a damageless mine.
   */
  damage?: number | { count: number; sides: number };
  /**
   * The damage type — **restricted to `kinetic`/`energy`/`explosive`** (rejected otherwise);
   * see {@link "@lancer-vtt/domain".MineProfile.damageType}. **Required exactly
   * when {@link damage} or {@link objectDamage} is authored** (an area-only mine — no
   * damage of any kind — omits it entirely); {@link spawnMine} enforces the pairing.
   */
  damageType?: DamageType;
  /** The forced-save axis label; omit for auto-hit (no save). */
  saveAxis?: string;
  /** The save DC — required when {@link saveAxis} is set. */
  saveDc?: number;
  /**
   * AP (Armor-Piercing): bypasses the target's Armor. Omit for the ordinary Armor-mitigated
   * default; see {@link "@lancer-vtt/domain".MineProfile.ap}.
   */
  ap?: boolean;
  /**
   * A separate pre-mitigation damage value auto-hit (no save) on every object caught in the
   * burst. Omit to leave objects unhit; see {@link
   * "@lancer-vtt/domain".MineProfile.objectDamage}.
   */
  objectDamage?: number;
  /**
   * The deploying combatant — omit for a GM-setup mine placed with no turn in progress, which
   * arms immediately instead of waiting on a deployer's turn-end.
   */
  deployedBy?: string;
  /**
   * An authorable override of the RAW default trigger predicate; mirrors {@link
   * "@lancer-vtt/domain".MineProfile.trigger} exactly (assigned straight onto it, see
   * below). Omit for the any-character default.
   */
  trigger?: "ally" | "remote" | "flying";
  /**
   * This mine creates a standing `AreaEffect` on detonation (Smoke Mine, Core p.106: fills its
   * Burst with soft cover, deals no damage at all); mirrors {@link
   * "@lancer-vtt/domain".MineProfile.areaGrants} exactly (assigned straight onto it, see below —
   * including reusing {@link burstRadius} as the created disk's radius, no second field). Omit
   * for a mine that creates no area (any mine that only deals damage).
   */
  areaGrants?: "soft_cover";
  /** One picked character instead of the burst; see {@link "@lancer-vtt/domain".MineProfile.singleTarget}. */
  singleTarget?: true;
  /** The deploying atom carries `on_fail`/`on_pass` clauses; see {@link "@lancer-vtt/domain".MineProfile.clauses}. Counts as a payload. */
  clauses?: true;
  /** What sets it off; see {@link "@lancer-vtt/domain".MineProfile.reach}. */
  reach?: "over";
  /** Who the detonation catches; see {@link "@lancer-vtt/domain".MineProfile.catches}. */
  catches?: "detonator";
  /** When it lapses unfired; see {@link "@lancer-vtt/domain".MineProfile.expires}. */
  expires?: "deployer_turn_start";
  /** The object it leaves when it goes off; see {@link "@lancer-vtt/domain".MineProfile.leaves}. */
  leaves?: { name: string; hp: number; evasion: number };
  /** Live at once rather than at the deployer's turn end (Javelin Rockets: "until the start of your next turn"). */
  armNow?: true;
  /** Not hidden: everyone sees it (Javelin Rockets: "All characters know which spaces you have chosen"). */
  visible?: true;
  /**
   * The deploying PLAYER's slot (a DIFFERENT namespace than {@link deployedBy}'s
   * combatant id): frozen onto {@link "@lancer-vtt/domain".Combatant.controllerSlotId} so
   * `visibleTo`'s owner-visibility branch admits the deployer for free ("Deployer + GM see
   * the mine" — straight out of the shipped predicate, no mine-specific redaction code).
   * `null`/omitted (the default, mirroring {@link spawnObject}'s GM-owned convention) means
   * a GM-authored mine no player deployed — only the GM sees it until detected.
   */
  controllerSlotId?: string | null;
  /**
   * The DEPLOYER's side — see {@link SpawnObjectArgs.allegiance}. Defaults to `"npc"`.
   *
   * **Independent of {@link MineProfile.trigger}'s `"ally"` resolution, which is keyed on
   * {@link deployedBy}** — that predicate deliberately resolves against the *deployer combatant*
   * rather than the mine's own side. It is the more precise read (a specific combatant, not a
   * side) and it is what makes a GM-setup mine with no deployer fail closed. This field answers
   * a different question: what every OTHER `sideOf` consumer sees.
   */
  allegiance?: Side;
};

/**
 * Spawn a **mine** combatant (Core p.106's MINE tag) — a `deployable`-kind board entity
 * riding the SIZE-1 object primitive ({@link spawnObject}) verbatim: 5 Evasion, 10 HP, no
 * compendium lookup. Layers on top of that plain object shape:
 *
 *   - **`concealed: true`** always — a mine rides the `concealed`/`projectEncounterFor`
 *     fog-of-war redaction verbatim; a non-owner/non-GM client's projected state omits it
 *     entirely until a reveal step (detonation, detection, disarm) clears the flag.
 *   - **`mineProfile`** — `armed` starts `false` when {@link SpawnMineArgs.deployedBy}
 *     is given (arms later, at that combatant's own turn-end) or `true` immediately when
 *     it is omitted (Core p.106: a GM-setup mine placed with no turn in progress has no
 *     future turn-end to wait for), plus the frozen detonation payload.
 *
 * Not a standard attack target — a mine's removal paths are detonate and disarm, never a direct
 * attack; the object primitive's ordinary destroy path is technically reachable but no attack
 * affordance is built for it.
 *
 * Returns a rejection for the same structurally-invalid inputs {@link spawnObject}
 * would (there is no compendium lookup to fail), plus mine-specific validation:
 * `burstRadius` must be positive, an AUTHORED `damage` must be non-negative-sane (a dice
 * `damage` spec's `count`/`sides` must be positive) — `damage` itself is OPTIONAL (a damageless
 * mine, validated not at all, is legal) — `saveAxis` requires `saveDc`, and, when authored,
 * `damageType` must be one of the three physical types (see {@link
 * SpawnMineArgs.damageType}'s doc).
 *
 * **At least one payload is required.** A mine authoring NONE of `damage`, `objectDamage`,
 * `areaGrants` or `clauses` is rejected: a detonation that does *nothing* observable but reveal itself is a
 * content-authoring mistake, not a legitimate design. Every real mine shipped or planned carries
 * at least one.
 *
 * **`damageType` is required exactly when `damage` or `objectDamage` is authored.**
 * An area-only mine (the Smoke Mine's exact shape) omits it entirely — nothing at
 * detonation ever reads it on that path. A mine authoring `damage` or `objectDamage`
 * WITHOUT a `damageType` is rejected: that value feeds `mitigate()`/the damage cascade,
 * which genuinely need a type, so an omission there is a content mistake, not legal design.
 */
export function spawnMine(args: SpawnMineArgs): SpawnResult {
  const { combatantId, name, burstRadius, damage, damageType, saveAxis, saveDc, ap, objectDamage, deployedBy, trigger, areaGrants, singleTarget, clauses, reach, catches, expires, leaves, armNow, visible, controllerSlotId = null, allegiance } = args;
  if (burstRadius <= 0) return err(`spawnMine: burstRadius must be positive (got ${burstRadius})`);
  if (damage === undefined && objectDamage === undefined && areaGrants === undefined && clauses === undefined) {
    return err(`spawnMine: ${name} carries no payload at all — author at least one of damage, objectDamage, areaGrants, or on_fail/on_pass clauses`);
  }
  if ((damage !== undefined || objectDamage !== undefined) && damageType === undefined) {
    return err(
      `spawnMine: ${name} authors a damage payload (damage or objectDamage) with no damageType — ` +
        `a damage payload requires a damageType (an area-only mine may omit both)`,
    );
  }
  // `damage` is `number | { count, sides }` (mirrors `MineProfile.damage`) — the flat arm gets a
  // non-negative check, the dice arm validates `count`/`sides` are both positive (a 0-or-negative
  // die roll is structurally meaningless, not merely "no damage"). `damage` is OPTIONAL: a
  // genuinely damageless mine (the Smoke Mine's shape) skips this validation entirely rather than
  // being coerced through a `damage: 0` that would still emit a save + a zero `hp_delta` at
  // detonation.
  if (damage !== undefined) {
    if (typeof damage === "number") {
      if (damage < 0) return err(`spawnMine: damage must be non-negative (got ${damage})`);
    } else {
      if (damage.count <= 0) return err(`spawnMine: damage.count must be positive (got ${damage.count})`);
      if (damage.sides <= 0) return err(`spawnMine: damage.sides must be positive (got ${damage.sides})`);
    }
  }
  if (saveAxis !== undefined && saveDc === undefined) {
    return err(`spawnMine: saveAxis "${saveAxis}" requires saveDc`);
  }
  if (objectDamage !== undefined && objectDamage < 0) {
    return err(`spawnMine: objectDamage must be non-negative (got ${objectDamage})`);
  }
  // When authored, `damageType` must still be one of the three physical types (see the
  // field's own doc) — omitted entirely (an area-only mine) skips this check, not `""`/`null`.
  if (damageType !== undefined && damageType !== "kinetic" && damageType !== "energy" && damageType !== "explosive") {
    return err(
      `spawnMine: damageType must be kinetic, energy, or explosive (got "${damageType}") — v1's ownerless ` +
        `burst reuses the simple mitigate-then-cascade shape (resolveMeltdownBlast/resolveDangerousTerrainCheck), ` +
        `not the full weapon damage-type router burn/heat need`,
    );
  }

  const built = spawnObject({ combatantId, name, size: 1, kind: "deployable", ...(allegiance !== undefined ? { allegiance } : {}) });
  if (!built.ok) return built;

  const mineProfile: MineProfile = {
    // No deployer/turn in progress at deploy time ⇒ arms immediately.
    armed: deployedBy === undefined || armNow === true,
    burstRadius,
    // Omitted entirely (not `damageType: undefined` as an own-enumerable key) for an area-only
    // mine — mirrors every other optional-field freeze in this file. The guard above already
    // proved it's present whenever `damage`/`objectDamage` is.
    ...(damageType !== undefined ? { damageType } : {}),
    // `damage` omitted entirely (not `damage: undefined` as an own-enumerable key) when the
    // caller doesn't author one — mirrors every other optional-field freeze in this file.
    ...(damage !== undefined ? { damage } : {}),
    ...(saveAxis !== undefined ? { saveAxis } : {}),
    ...(saveDc !== undefined ? { saveDc } : {}),
    ...(ap !== undefined ? { ap } : {}),
    ...(objectDamage !== undefined ? { objectDamage } : {}),
    ...(deployedBy !== undefined ? { deployedBy } : {}),
    // Authorable trigger-predicate override; omitted ⇒ the any-character default.
    ...(trigger !== undefined ? { trigger } : {}),
    // An area payload, created on detonation instead of (or beside) damage.
    ...(areaGrants !== undefined ? { areaGrants } : {}),
    ...(singleTarget !== undefined ? { singleTarget } : {}),
    ...(clauses !== undefined ? { clauses } : {}),
    ...(reach !== undefined ? { reach } : {}),
    ...(catches !== undefined ? { catches } : {}),
    ...(expires !== undefined ? { expires } : {}),
    ...(leaves !== undefined ? { leaves } : {}),
  };

  return ok({
    ...built.value,
    concealed: visible !== true, // a mine is hidden by default, riding the fog-of-war redaction verbatim.
    controllerSlotId, // the deploying PLAYER's slot (visibleTo's owner-visibility branch) — null (default) = GM-only until detected.
    mineProfile,
  });
}

// ── mine-from-gear adapter ─────────────────────────────────────────────────────

/**
 * The (loosely-typed, passthrough) compendium pilot-gear `deployables[]` entry shape
 * (`@massif/lancer-data` — see `pg_thermite_charge`): a MINE-type item's own detonation
 * payload nests HERE, distinct from the item's top-level `actions[]` {@link RawActionBearer}
 * reads (the Frag-Grenade shape `freezePilotGearActionIds` consumes).
 */
type RawDeployable = {
  type?: unknown;
  /** `[{ type: "Burst", val: N }]` — every sibling Mine-type deployable in `@massif/lancer-data` carries this; `pg_thermite_charge` notably doesn't (see {@link gearDeployableMineProfile}'s doc). */
  range?: Array<{ type?: unknown; val?: unknown }>;
  actions?: Array<{
    name?: unknown;
    damage?: Array<{ type?: unknown; val?: unknown; save?: unknown; ap?: unknown }>;
  }>;
};

/**
 * Normalize a compendium damage/range `val` to a flat non-negative integer. Pilot-gear/
 * deployable numbers are plain numbers (or numeric strings), never a mech-weapon dice
 * expression — {@link parseDamageVal}'s `NdM` grammar doesn't apply here.
 */
function numericDeployableVal(val: unknown): number {
  if (typeof val === "number" && Number.isFinite(val)) return Math.max(0, Math.trunc(val));
  if (typeof val === "string") {
    const n = parseInt(val, 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

/**
 * Normalize a compendium `save` abbreviation (`"eng"`/`"agi"`/`"hull"`/`"sys"`) to the engine's
 * full-word save-axis convention ({@link "@lancer-vtt/domain".SaveStep.against}: `"hull" |
 * "agility" | "systems" | "engineering"`). Since `decisions/0163` the axis is **read**, not a
 * label: `resolveMineDetonation` resolves the defender's bonus through
 * `saveBonusFor(target, saveAxis)`, so an unrecognized abbreviation names no field on the base
 * and lands on the `+0` floor (`:1090`) rather than silently rolling some other axis — which
 * is why normalizing the common four here matters now, not just for readable warning text.
 */
function normalizeSaveAxis(save: string): string {
  switch (save.toLowerCase()) {
    case "eng":
      return "engineering";
    case "agi":
      return "agility";
    case "sys":
      return "systems";
    case "hull":
      return "hull";
    default:
      return save.toLowerCase();
  }
}

/**
 * The subset of {@link SpawnMineArgs} {@link gearDeployableMineProfile} derives from a
 * compendium item's own data — everything EXCEPT the deploy-time identity/placement/ownership
 * fields (`combatantId`/`name`/`deployedBy`/`controllerSlotId`) and `saveDc` (a deployer-stat
 * concern — `interpret.ts`'s `defaultSaveDc` convention computes a forced-save DC off the
 * SOURCE's own frozen `saveTarget` (the printed/computed save target, `decisions/0163`) —
 * not something the gear item's own data
 * carries; the caller supplies it).
 */
export type GearMineProfileArgs = Pick<SpawnMineArgs, "burstRadius" | "damage" | "damageType" | "saveAxis" | "ap" | "objectDamage">;

/**
 * Build a mine's detonation payload from its OWN compendium data — no hand-typed magic numbers
 * in the deploy path. Reads `pilotGearId`'s `deployables[0]`:
 *
 *   - The FIRST action carrying a `save`-qualified damage entry is the character-facing
 *     detonation (Thermite Charge's "Detonate", 3 Energy AP vs. Engineering). Its `ap` flag
 *     is read straight off that damage entry too — `@massif/lancer-data` genuinely carries
 *     `ap: true` on Thermite Charge's damage entries, so no hand-typed constant is needed here
 *     (unlike `burstRadius` below).
 *   - The object-facing detonation is the FIRST *other* action whose damage carries no
 *     `save` (auto-hit) — Thermite Charge's sibling "Detonate (vs. Object)", 10 Energy AP,
 *     `objectDamage` in {@link "@lancer-vtt/domain".MineProfile}. `undefined` (an item with
 *     no such sibling action) leaves objects unhit, per that field's default.
 *
 * `damage`/`damageType`/`saveAxis`/`ap`/`objectDamage` are all genuinely structured,
 * machine-readable fields on `pg_thermite_charge`'s `deployables[0]` — checked directly
 * against `@massif/lancer-data`, not assumed from the burst-radius gap below.
 *
 * **`burstRadius` is a KNOWN UPSTREAM DATA GAP**: every
 * OTHER Mine-type `deployables[]` entry in `@massif/lancer-data` (`systems.json`'s Pattern-A/B/
 * Flash/Mesmer/Bouncing mines) carries a structured `range: [{ type: "Burst", val: N }]` array
 * — read here FIRST when present — but `pg_thermite_charge`'s own `deployables[0]` omits that
 * field entirely, even though its `effect` prose plainly states "Burst 1." Falls back to that
 * RAW-documented constant ONLY when `range` is absent on a genuinely `type: "Mine"` deployable —
 * one named, commented constant for the one item missing structured data, not a prose-scraping
 * parser (regexing the `effect` HTML string would be far more fragile). A future mine item
 * shipping the same gap degrades the same honest way.
 *
 * Returns an error (never throws) for an unresolved `pilotGearId`, a non-Mine deployable, or a
 * deployable with no readable damage entry — mirrors every other freeze/build function's
 * honest-degrade discipline.
 */
export function gearDeployableMineProfile(pilotGearId: string, compendium: Compendium): Result<GearMineProfileArgs, string> {
  const item = compendium.getById("pilot_gear", pilotGearId) as (RawPilotGear & { deployables?: RawDeployable[] }) | undefined;
  if (!item) return err(`gearDeployableMineProfile: no such pilot-gear item "${pilotGearId}"`);

  const deployable = item.deployables?.[0];
  if (!deployable || typeof deployable.type !== "string" || deployable.type.toLowerCase() !== "mine") {
    return err(`gearDeployableMineProfile: "${pilotGearId}" carries no Mine-type deployables[0]`);
  }

  const actions = Array.isArray(deployable.actions) ? deployable.actions : [];
  const charAction =
    actions.find((a) => Array.isArray(a.damage) && a.damage.some((d) => typeof d.save === "string")) ??
    actions.find((a) => Array.isArray(a.damage) && a.damage.length > 0);
  const dmg = charAction?.damage?.[0];
  if (!dmg) return err(`gearDeployableMineProfile: "${pilotGearId}"'s deployables[0] carries no readable damage entry`);

  // The sibling auto-hit (no-save) action, distinct from `charAction`, is the object-facing
  // detonation ("Detonate (vs. Object)" on Thermite Charge).
  const objectAction = actions.find(
    (a) => a !== charAction && Array.isArray(a.damage) && a.damage.length > 0 && a.damage.every((d) => typeof d.save !== "string"),
  );
  const objDmg = objectAction?.damage?.[0];

  const burstRange = deployable.range?.find((r) => typeof r.type === "string" && r.type.toLowerCase() === "burst");
  const burstVal = burstRange ? numericDeployableVal(burstRange.val) : 0;
  let burstRadius = burstVal;
  if (burstRadius <= 0) {
    // Data-gap shim. This Mine deployable omits the structured
    // `range: [{ type: "Burst", val }]` array every sibling Mine in `@massif/lancer-data`
    // carries — pg_thermite_charge's own data has the burst only in `effect` prose ("Burst 1").
    // Fall back to RAW-prose Burst 1, but LOUD (warn-and-default, never throw) so the shim stays
    // visible until upstream fixes the gap — then `burstRange` takes over and this branch goes dead.
    console.warn(
      `gearDeployableMineProfile: Mine deployable "${pilotGearId}" has no structured Burst range — ` +
        `falling back to RAW-prose Burst 1 (upstream @massif/lancer-data data gap)`,
    );
    burstRadius = 1;
  }

  const result: GearMineProfileArgs = {
    burstRadius,
    damage: numericDeployableVal(dmg.val),
    damageType: normalizeDamageType(dmg.type),
    ...(typeof dmg.save === "string" ? { saveAxis: normalizeSaveAxis(dmg.save) } : {}),
    ...(dmg.ap === true ? { ap: true } : {}),
    ...(objDmg ? { objectDamage: numericDeployableVal(objDmg.val) } : {}),
  };
  return ok(result);
}

/** Args for {@link spawnMineFromGear} — the deploy-time identity/placement/ownership fields the compendium item's own data can't supply, plus the compendium lookup itself. */
export type SpawnMineFromGearArgs = {
  combatantId: string;
  name: string;
  pilotGearId: string;
  compendium: Compendium;
  /** Required only when the gear's damage entry carries a save axis — see {@link GearMineProfileArgs}'s doc on why this isn't gear data. */
  saveDc?: number;
  deployedBy?: string;
  controllerSlotId?: string | null;
  /** The DEPLOYER's side — see {@link SpawnObjectArgs.allegiance}. Defaults to `"npc"`. */
  allegiance?: Side;
};

/**
 * Spawn a mine straight from its OWN compendium data —
 * {@link gearDeployableMineProfile} + {@link spawnMine} composed into one call, so a future
 * "deploy this gear item" caller (a room handler, a CLI verb) never hand-assembles the burst/
 * damage/save numbers itself. Propagates the builder's lookup error verbatim on a bad
 * `pilotGearId`/non-Mine item; otherwise identical to calling `spawnMine` with the builder's
 * output plus the caller-supplied deploy-time fields.
 */
export function spawnMineFromGear(args: SpawnMineFromGearArgs): SpawnResult {
  const { combatantId, name, pilotGearId, compendium, saveDc, deployedBy, controllerSlotId, allegiance } = args;
  const profile = gearDeployableMineProfile(pilotGearId, compendium);
  if (!profile.ok) return profile;
  return spawnMine({
    combatantId,
    name,
    ...profile.value,
    ...(saveDc !== undefined ? { saveDc } : {}),
    ...(deployedBy !== undefined ? { deployedBy } : {}),
    ...(controllerSlotId !== undefined ? { controllerSlotId } : {}),
    ...(allegiance !== undefined ? { allegiance } : {}),
  });
}
